import L from 'leaflet';
import { useEffect, useRef, useState } from 'react';

import type { DeliveryQuote, DeliveryZone } from '../api/types.ts';
import { BELGRADE, describePoint, searchAddress, type AddressSuggestion } from '../core/geocoding.ts';
import { formatPara, para } from '../core/money.ts';
import { errorText, useLang, useStrings, type Strings } from '../i18n/index.ts';
import { addAddress, deliveryZones, quoteForPoint, setGuestAndRefresh } from '../state/address-book.ts';
import { popRoute } from '../state/router.ts';
import { isSignedIn } from '../state/session.ts';
import { Icon } from '../ui/icons.tsx';
import { Button, cx, ErrorBanner, FieldBox, Spinner } from '../ui/kit.tsx';
import { osmTileLayer } from '../ui/leaflet.ts';
import { distanceM, type LatLng } from '../ui/route.ts';
import { Screen } from '../ui/Screen.tsx';
import '../styles/account.css';

/** Старт на уровне улиц: булавка должна встать на конкретный дом, а не на подпись города. */
const START_ZOOM = 16;
/** Масштаб после «где я» и выбора подсказки: виден дом и соседние. */
const POINT_ZOOM = 17;
/** Сдвиг центра меньше этого — пересчёт кадра карты (клавиатура, панель), а не жест. */
const MOVE_THRESHOLD_M = 2;
const SEARCH_DELAY_MS = 450;
const DESCRIBE_DELAY_MS = 400;
const MIN_QUERY_LENGTH = 3;

function clean(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/** Внешние контуры зон в координатах карты. */
function zoneRings(zones: DeliveryZone[]): L.LatLngTuple[][] {
  const rings: L.LatLngTuple[][] = [];
  for (const zone of zones) {
    for (const polygon of zone.polygon.coordinates) {
      const outer = polygon[0];
      if (!outer) continue;
      const ring: L.LatLngTuple[] = [];
      for (const [lng, lat] of outer) {
        if (lat !== undefined && lng !== undefined) ring.push([lat, lng]);
      }
      if (ring.length >= 3) rings.push(ring);
    }
  }
  return rings;
}

function zoneText(strings: Strings, quote: DeliveryQuote | null): string {
  if (!quote) return strings.loading;
  if (!quote.covered || !quote.tariff) return strings.outOfZone;
  const freeFrom = para(quote.tariff.freeFromRsd);
  const terms = freeFrom > 0n ? strings.freeFrom(formatPara(freeFrom)) : strings.deliverHere;
  // Время до этой самой двери: путь от склада известен до сохранения адреса.
  return quote.etaMinutes === null ? terms : `${strings.etaAbout(quote.etaMinutes)} · ${terms}`;
}

/**
 * Новый адрес: карта с булавкой по центру, поиск улицы и проверка зоны доставки.
 *
 * Карта открыта сразу: поиск знает не каждый адрес, а точку всегда можно поставить рукой.
 * Зона проверяется до сохранения, чтобы «сюда не доставляем» не всплыло на оформлении. Гость
 * хранит точку в браузере; в аккаунт она сохраняется при входе.
 *
 * Без пропсов это экран стека; с `embedded` — тело листа, который закрывает себя в `onSaved`.
 */
export function NewAddressScreen(props: { onSaved?: () => void; title?: string; embedded?: boolean } = {}) {
  const strings = useStrings();
  const lang = useLang();

  const mapElement = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const meMarker = useRef<L.Marker | null>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const point = useRef<LatLng>({ lat: BELGRADE.lat, lng: BELGRADE.lng });
  // Камеру уже поставили геопозиция или подсказка: зоны её к себе не тянут.
  const cameraPlaced = useRef(false);
  const checkRun = useRef(0);
  const describeRun = useRef(0);
  const describeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const describeAbort = useRef<AbortController | null>(null);
  const live = useRef({ lang, strings });
  const lastQuote = useRef<DeliveryQuote | null>(null);
  live.current = { lang, strings };

  const [addressText, setAddressText] = useState('');
  const [comment, setComment] = useState('');
  const [entrance, setEntrance] = useState('');
  const [floor, setFloor] = useState('');
  const [apartment, setApartment] = useState('');
  const [intercom, setIntercom] = useState('');
  const [quote, setQuote] = useState<DeliveryQuote | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [locationDenied, setLocationDenied] = useState(false);

  const checkZone = async (target: LatLng, text?: string) => {
    const run = ++checkRun.current;
    setIsChecking(true);
    try {
      const next = await quoteForPoint(target.lat, target.lng, text);
      if (run !== checkRun.current) return;
      lastQuote.current = next;
      setQuote(next);
      setError(null);
    } catch (failure) {
      if (run === checkRun.current) setError(errorText(live.current.strings, failure));
    } finally {
      if (run === checkRun.current) setIsChecking(false);
    }
  };

  const cancelDescribe = () => {
    describeRun.current += 1;
    clearTimeout(describeTimer.current);
    describeAbort.current?.abort();
    describeAbort.current = null;
  };

  /** Карту сдвинули: обновляются и зона, и название точки. Название ждёт паузы в движении. */
  const refreshPoint = () => {
    const target = point.current;
    void checkZone(target);
    cancelDescribe();
    const run = describeRun.current;
    describeTimer.current = setTimeout(() => {
      const abort = new AbortController();
      describeAbort.current = abort;
      void describePoint(target.lat, target.lng, live.current.lang, abort.signal).then((described) => {
        if (!described || run !== describeRun.current) return;
        setAddressText(described);
        // Точка вне зон: повтор запроса с подписью, чтобы уведомление «начали доставлять» назвало адрес.
        if (lastQuote.current?.covered === false) void quoteForPoint(target.lat, target.lng, described).catch(() => undefined);
      });
    }, DESCRIBE_DELAY_MS);
  };

  const moveCamera = (target: LatLng) => {
    cameraPlaced.current = true;
    map.current?.setView([target.lat, target.lng], POINT_ZOOM);
  };

  /** Отказ в геопозиции не мешает: точку можно поставить рукой. */
  const locate = () => {
    if (!('geolocation' in navigator)) {
      setLocationDenied(true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const found = { lat: position.coords.latitude, lng: position.coords.longitude };
        setLocationDenied(false);
        if (!map.current) return;
        if (!meMarker.current) {
          const icon = L.divIcon({ className: 'address-me', iconSize: [18, 18], iconAnchor: [9, 9] });
          meMarker.current = L.marker([found.lat, found.lng], { icon, interactive: false, keyboard: false }).addTo(map.current);
        } else {
          meMarker.current.setLatLng([found.lat, found.lng]);
        }
        moveCamera(found);
      },
      (failure) => {
        if (failure.code === failure.PERMISSION_DENIED) setLocationDenied(true);
      },
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 10_000 },
    );
  };

  useEffect(() => {
    const element = mapElement.current;
    if (!element) return;
    let disposed = false;

    const created = L.map(element, { center: [BELGRADE.lat, BELGRADE.lng], zoom: START_ZOOM, zoomControl: false });
    osmTileLayer().addTo(created);
    map.current = created;

    created.on('moveend', () => {
      const center = created.getCenter();
      const next = { lat: center.lat, lng: center.lng };
      if (distanceM(next, point.current) <= MOVE_THRESHOLD_M) return;
      point.current = next;
      refreshPoint();
    });

    // Высота панели и клавиатура меняют кадр карты: центр остаётся под булавкой.
    const resize = new ResizeObserver(() => created.invalidateSize());
    resize.observe(element);

    refreshPoint();

    // Зоны — украшение: без них карта работает.
    void deliveryZones()
      .then((zones) => {
        if (disposed) return;
        const rings = zoneRings(zones);
        if (rings.length === 0) return;
        const layer = L.polygon(
          rings.map((ring) => [ring]),
          { className: 'address-zone', interactive: false },
        ).addTo(created);
        // Где человек, никто не сказал: вместо центра города показывается, куда доставляем.
        if (!cameraPlaced.current) {
          cameraPlaced.current = true;
          created.fitBounds(layer.getBounds().pad(0.08));
        }
      })
      .catch(() => undefined);

    // Разрешение на геопозицию без жеста не запрашивается: браузеры такие запросы глушат.
    // Уже выданное разрешение сразу ведёт карту к человеку.
    void navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((status) => {
        if (disposed) return;
        if (status.state === 'granted') locate();
        else if (status.state === 'denied') setLocationDenied(true);
      })
      .catch(() => undefined);

    return () => {
      disposed = true;
      cancelDescribe();
      checkRun.current += 1;
      resize.disconnect();
      created.remove();
      map.current = null;
      meMarker.current = null;
    };
    // Карта создаётся один раз на жизнь экрана.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Подсказки ждут паузы во вводе; устаревший запрос отменяется.
  useEffect(() => {
    const text = query.trim();
    if (text.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      return;
    }
    const abort = new AbortController();
    const timer = setTimeout(() => {
      void searchAddress(text, lang, abort.signal).then((found) => {
        if (!abort.signal.aborted) setSuggestions(found);
      });
    }, SEARCH_DELAY_MS);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [query, lang]);

  const clearSearch = () => {
    setQuery('');
    searchInput.current?.blur();
  };

  const pick = (suggestion: AddressSuggestion) => {
    const target = { lat: suggestion.lat, lng: suggestion.lng };
    // Название берётся из подсказки: подпись точки с карты его уже не заменит.
    cancelDescribe();
    setAddressText(suggestion.subtitle ? `${suggestion.title}, ${suggestion.subtitle}` : suggestion.title);
    point.current = target;
    clearSearch();
    moveCamera(target);
    void checkZone(target, suggestion.subtitle ? `${suggestion.title}, ${suggestion.subtitle}` : suggestion.title);
  };

  const covered = quote?.covered === true;
  // Пока идёт проверка, условия на экране относятся к прежней точке.
  const canSave = addressText !== '' && covered && !isChecking;

  const save = async () => {
    if (!canSave || isSaving) return;
    setError(null);
    setIsSaving(true);
    const details = { entrance: clean(entrance), floor: clean(floor), apartment: clean(apartment), intercom: clean(intercom) };
    try {
      const place = { text: addressText, lat: point.current.lat, lng: point.current.lng, comment: clean(comment), ...details };
      if (isSignedIn()) await addAddress(place);
      else await setGuestAndRefresh(place);
      props.onSaved?.();
      if (!props.embedded) popRoute();
    } catch (failure) {
      setError(errorText(strings, failure));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Screen noDock overlayNav embedded={props.embedded ?? false} closeIcon={props.embedded === true && props.title !== undefined} className="address-new">
      <div className="address-new-body">
        <div className="address-map-box">
          <div className="address-map">
            <div ref={mapElement} />
          </div>
          {/* Булавка стоит в центре: двигать карту одним пальцем точнее, чем двигать булавку. */}
          <span className="address-pin" aria-hidden="true" />

          <div className="address-nav-title">
            <span>{props.title ?? strings.newAddress}</span>
          </div>

          <div className="address-search">
            <div className="row address-search-field">
              <Icon name="search" size={18} className="t-muted" />
              <input
                ref={searchInput}
                className="plain-input"
                type="text"
                enterKeyHint="search"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder={strings.searchAddressPlaceholder}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              {query !== '' && (
                <button type="button" className="address-search-clear" aria-label={strings.a11yClearText} onClick={clearSearch}>
                  <Icon name="close" size={12} weight={3} />
                </button>
              )}
              <button type="button" className={cx('address-locate', locationDenied && 'is-denied')} aria-label={strings.locateMe} onClick={locate}>
                <Icon name="locate" size={20} />
              </button>
            </div>

            {suggestions.length > 0 && (
              <div className="address-suggestions">
                {suggestions.map((suggestion) => (
                  <button key={suggestion.id} type="button" onClick={() => pick(suggestion)}>
                    <span className="t-subheadline">{suggestion.title}</span>
                    {suggestion.subtitle && <span className="t-caption t-muted">{suggestion.subtitle}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="address-panel">
          <form
            className="readable stack"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <div className={cx('row', 'address-zone-badge', quote !== null && (covered ? 'is-covered' : 'is-out'))}>
              {isChecking ? <Spinner size={16} /> : quote !== null && <Icon name={covered ? 'check' : 'warning'} size={16} weight={2.6} />}
              <span>{zoneText(strings, quote)}</span>
            </div>

            <p className={cx('t-display', 'address-name', addressText === '' && 't-muted')}>{addressText || strings.dragPinHint}</p>

            {/* Подъезд, этаж, квартира и домофон отдельными полями: курьер читает их крупно у двери. */}
            <div className="address-fields">
              <FieldBox label={strings.addressEntrance} muted>
                <input type="text" autoComplete="off" value={entrance} onChange={(event) => setEntrance(event.target.value)} />
              </FieldBox>
              <FieldBox label={strings.addressFloor} muted>
                <input type="text" autoComplete="off" value={floor} onChange={(event) => setFloor(event.target.value)} />
              </FieldBox>
              <FieldBox label={strings.addressApartment} muted>
                <input type="text" autoComplete="off" value={apartment} onChange={(event) => setApartment(event.target.value)} />
              </FieldBox>
              <FieldBox label={strings.addressIntercom} muted>
                <input type="text" autoComplete="off" value={intercom} onChange={(event) => setIntercom(event.target.value)} />
              </FieldBox>
            </div>

            <FieldBox label={strings.entranceComment} muted>
              <input
                type="text"
                autoComplete="off"
                placeholder={strings.entranceCommentPlaceholder}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
            </FieldBox>

            {error && <ErrorBanner text={error} />}

            <Button type="submit" disabled={!canSave || isSaving}>
              {isSaving ? strings.loading : strings.deliverHere}
            </Button>
          </form>
        </div>
      </div>
    </Screen>
  );
}
